// 章节在"方案文档"里怎么排、标题怎么写——服务端拼文档/拆文档与前端预览/批注定位共用这一份规则，
// 两边各写一套迟早会对不上（对不上的后果是批注落错章、用户编辑后某一章被整段吞掉）。

export interface ChapterLike {
  num: string;
  title: string;
}

/** 前置内容（缩略语表）的章节号。它要读完全部章节才能生成，所以在生成顺序里排最后；
 *  但在方案文档里它属于正文之前的前置内容，要排在最前面，标题也不带章节号。 */
export const FRONT_MATTER_NUM = "0";

export function isFrontMatter(num: string): boolean {
  return num === FRONT_MATTER_NUM;
}

/** 文档里的章标题文字（不含 `## `）。 */
export function chapterHeading(c: ChapterLike): string {
  return isFrontMatter(c.num) ? c.title : `${c.num} ${c.title}`;
}

/** 文档中的排列顺序：前置内容在前，其余保持章节表（生成）顺序。 */
export function documentOrder<T extends ChapterLike>(chapters: T[]): T[] {
  return [...chapters.filter((c) => isFrontMatter(c.num)), ...chapters.filter((c) => !isFrontMatter(c.num))];
}

/** 判断一行是不是某一章的章标题行（`## {章节号} {标题}`，前置内容为 `## {标题}`）。 */
export function matchChapterHeading<T extends ChapterLike>(line: string, chapters: T[]): T | undefined {
  const m = line.match(/^##\s+(.+?)\s*$/);
  if (!m) return undefined;
  const text = m[1]!;
  const first = text.split(/\s+/)[0];
  return (
    chapters.find((c) => !isFrontMatter(c.num) && c.num === first) ??
    chapters.find((c) => isFrontMatter(c.num) && text === c.title)
  );
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** 章节正文开头若自带本章的章标题（如 "## 2 引言（Introduction）"），拼文档时去掉——
 *  文档统一加章标题，不去掉就会出现上下两行标题。只看第一行，且只认本章的章号，
 *  避免误删正文里的小节标题。 */
export function stripOwnHeading(c: ChapterLike, content: string): string {
  const text = content.replace(/\r\n/g, "\n").trim();
  const newline = text.indexOf("\n");
  const first = newline === -1 ? text : text.slice(0, newline);
  const pattern = isFrontMatter(c.num)
    ? new RegExp(`^#{1,6}\\s*${escapeRegExp(c.title.split("（")[0]!.trim())}`)
    : new RegExp(`^#{1,6}\\s*${escapeRegExp(c.num)}\\.?(\\s|$)`);
  if (!pattern.test(first)) return text;
  return newline === -1 ? "" : text.slice(newline + 1).trim();
}
